/**
 * MODULE: domain/sales-presentations/frontend/components/PresentationPostToolbar
 * PURPOSE: E4-4 — Post button + last-posted line; loads publish channels; opens PresentationPostModal.
 * DEPENDENCIES: flowbite-react, heroicons, presentationsApi, PresentationPostModal, presentationPublishStorage
 * PUBLIC_API: PresentationPostToolbar, PostToolbarHandle, PresentationPostToolbarProps
 * SIDE_EFFECTS: GET publish channels; modal open/close
 * AGENT_SAFETY: SAFE
 * Recent: 2026-05-12 — [2.2.956] **`forwardRef`** **`PostToolbarHandle`**, **`showButton`** for overflow menu; pairs with **`EditorToolbar`**.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Tooltip } from 'flowbite-react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import type { PublishChannel, PublishPresentationResponse } from '../api/client';
import { presentationsApi } from '../api/client';
import type { RenderedSlide } from '../api/types';
import { PresentationPostModal } from './PresentationPostModal';
import {
  formatPostedAgo,
  readLastPublish,
  writeLastPublish,
  POST_TOOLTIP_NO_CHANNEL,
  POST_TOOLTIP_NO_SLIDES,
} from '../utils/presentationPublishStorage';

export interface PostToolbarHandle {
  openModal: () => void;
}

export interface PresentationPostToolbarProps {
  presentationId: string;
  deckTitle: string;
  slides: RenderedSlide[];
  /** When false, the Post button is hidden (E4-7 overflow menu opens the modal via ref). Default true. */
  showButton?: boolean;
  /** Ghost chrome for editor toolbar; default solid gray. */
  buttonVariant?: 'solid' | 'ghost';
}

export const PresentationPostToolbar = forwardRef<PostToolbarHandle, PresentationPostToolbarProps>(
  function PresentationPostToolbar(
    { presentationId, deckTitle, slides, showButton = true, buttonVariant = 'solid' },
    ref
  ) {
    const [channels, setChannels] = useState<PublishChannel[]>([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [lastPublish, setLastPublish] = useState(() => readLastPublish(presentationId));

    const slideCount = slides.length;
    const hasChannel = channels.length > 0;
    const canPost = hasChannel && slideCount >= 1;

    const loadChannels = useCallback(async () => {
      setChannelsLoading(true);
      try {
        const rows = await presentationsApi.listPublishChannels();
        setChannels(Array.isArray(rows) ? rows : []);
      } catch {
        setChannels([]);
      } finally {
        setChannelsLoading(false);
      }
    }, []);

    useEffect(() => {
      void loadChannels();
    }, [loadChannels]);

    useEffect(() => {
      setLastPublish(readLastPublish(presentationId));
    }, [presentationId]);

    const onPublished = useCallback(
      (meta: PublishPresentationResponse) => {
        writeLastPublish(presentationId, {
          channelName: meta.channelName,
          postedAt: meta.postedAt,
          postUrl: meta.postUrl ?? null,
        });
        setLastPublish(readLastPublish(presentationId));
      },
      [presentationId]
    );

    const openModal = useCallback(() => {
      void loadChannels();
      setModalOpen(true);
    }, [loadChannels]);

    useImperativeHandle(ref, () => ({ openModal }), [openModal]);

    const ghostClasses =
      buttonVariant === 'ghost'
        ? 'border-0 bg-transparent shadow-none ring-0 hover:bg-gray-100 dark:hover:bg-gray-700'
        : undefined;

    const postButton = (
      <Button
        color={buttonVariant === 'ghost' ? 'light' : 'gray'}
        size="sm"
        data-testid="editor-post-button"
        disabled={!canPost || channelsLoading}
        onClick={openModal}
        className={ghostClasses}
      >
        <PaperAirplaneIcon className="mr-1 h-4 w-4" />
        Post
      </Button>
    );

    let wrapped = postButton;
    if (!hasChannel) {
      wrapped = (
        <Tooltip content={POST_TOOLTIP_NO_CHANNEL}>
          <span className="inline-flex">{postButton}</span>
        </Tooltip>
      );
    } else if (slideCount < 1) {
      wrapped = (
        <Tooltip content={POST_TOOLTIP_NO_SLIDES}>
          <span className="inline-flex">{postButton}</span>
        </Tooltip>
      );
    }

    return (
      <div className="flex flex-col items-end gap-0.5" data-testid="presentation-post-toolbar">
        {showButton ? wrapped : null}
        {lastPublish && (
          <span className="max-w-[14rem] truncate text-[10px] text-gray-500 dark:text-gray-400" data-testid="post-last-posted">
            Last posted to {lastPublish.channelName} {formatPostedAgo(lastPublish.postedAt)}
          </span>
        )}
        <PresentationPostModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          presentationId={presentationId}
          deckTitle={deckTitle}
          firstSlide={slides[0] ?? null}
          channels={channels}
          channelsLoading={channelsLoading}
          onPublished={onPublished}
        />
      </div>
    );
  }
);

PresentationPostToolbar.displayName = 'PresentationPostToolbar';
